import { captureException } from "@sentry/nextjs";
import { refreshToken } from "@website/fetcher/refresh-token";
import { authStore } from "@website/store/store";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import jwtDecode from "jwt-decode";
import { LEGACY_ACCESS_TOKEN_KEY, LEGACY_REFRESH_TOKEN_KEY } from "./constants";
import { B2BProfile, DECODED_TOKEN, UserInfo } from "./types";
import { getToken } from "./utils";

const isPositiveNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

type TokenType = "access" | "refresh" | "unknown";

type TokenErrorExtra = Record<string, string | number | boolean | undefined>;

const getTokenErrorTags = () => ({
  errorType: "tokenError",
  url: typeof window === "undefined" ? "" : window.location.href,
  referrer: typeof document === "undefined" ? "" : document.referrer,
  channel: getChannel() as Channels,
  appName: process.env.NEXT_PUBLIC_APP_NAME,
  appVersion: process.env.NEXT_PUBLIC_IMAGE_VERSION
});

export const captureTokenError = (message: string, extra?: TokenErrorExtra) => {
  if (typeof window === "undefined") return;
  captureException(message, {
    tags: getTokenErrorTags(),
    extra
  });
};

export const getLegacyTokens = () => {
  if (typeof window === "undefined") {
    return { accessToken: "", refreshToken: "" };
  }

  const localToken =
    localStorage.getItem(LEGACY_ACCESS_TOKEN_KEY) ||
    authStore.accessToken.get();
  const localRefreshToken =
    localStorage.getItem(LEGACY_REFRESH_TOKEN_KEY) ||
    authStore.refreshToken.get();

  return {
    accessToken: localToken || "",
    refreshToken: localRefreshToken || ""
  };
};

export const getTokenCookieMaxAgeSeconds = (
  token: string,
  refreshBufferMs = 0,
  tokenType: TokenType = "unknown"
): number | undefined => {
  try {
    const decoded: DECODED_TOKEN = jwtDecode(token);
    if (!decoded?.exp) {
      captureTokenError("expiration time not set in token", {
        tokenType,
        hasToken: Boolean(token),
        tokenLength: token.length,
        refreshBufferMs,
        decodedExpType: typeof decoded?.exp,
        decodedIatType: typeof decoded?.iat
      });
      return undefined;
    }
    const expiryMs = decoded.exp * 1000;
    const nowMs = Date.now();
    const secondsUntilExpiry = Math.floor((expiryMs - nowMs) / 1000);
    const maxAgeMs = expiryMs - nowMs - refreshBufferMs;

    const maxAgeSeconds = Math.floor(maxAgeMs / 1000);
    if (!Number.isFinite(maxAgeSeconds) || maxAgeSeconds <= 0) {
      captureTokenError("expiration time calculated negative number", {
        tokenType,
        hasToken: Boolean(token),
        tokenLength: token.length,
        exp: decoded.exp,
        iat: decoded.iat,
        nowSeconds: Math.floor(nowMs / 1000),
        secondsUntilExpiry,
        maxAgeSeconds,
        refreshBufferMs,
        refreshBufferSeconds: Math.floor(refreshBufferMs / 1000),
        isExpired: secondsUntilExpiry <= 0,
        isInsideRefreshBuffer: secondsUntilExpiry > 0 && maxAgeSeconds <= 0
      });
      return undefined;
    }

    return maxAgeSeconds;
  } catch {
    captureTokenError("expiration time could not be calculated", {
      tokenType,
      hasToken: Boolean(token),
      tokenLength: token.length,
      refreshBufferMs
    });
    return undefined;
  }
};

export const isTokenValid = (token?: string, tokenMaxAge?: number) =>
  Boolean(token) && isPositiveNumber(tokenMaxAge);

export const removeTokenFromUrl = () => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.searchParams.delete("token");

  window.history.replaceState({}, "", url.toString());
};

export const getTokenWithRefresh = async () => {
  const token = getToken();
  if (!token.stToken && token.refreshToken) {
    try {
      await refreshToken();
    } catch {
      return token;
    }
  }

  return getToken();
};

export const updateTokenVersion = () => {
  authStore.tokenVersion.set((authStore.tokenVersion.get() || 0) + 1);
};

export function hasB2BAction(
  profileType: DECODED_TOKEN["profileType"],
  profile: B2BProfile | UserInfo | undefined,
  actions: string | ReadonlyArray<string>
): boolean {
  const requiredActions = typeof actions === "string" ? [actions] : actions;

  return (
    profileType === "B2B" &&
    profile !== undefined &&
    "actions" in profile &&
    Boolean(
      profile.actions &&
        requiredActions.every((action) =>
          profile.actions.items.some((item) => item.name === action)
        )
    )
  );
}
